import { Stack, TextField, TextFieldProps } from '@mui/material';
import { CountrySelect } from 'components/CountrySelect';
import parsePhoneNumber, { PhoneNumber } from 'libphonenumber-js';
import { ChangeEvent, MutableRefObject, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from '../../hooks/useLanguage';
import { getCountries } from '../../utils/countries';

export type PhoneInputProps = {
  value: string;
  error?: boolean;
  helperText?: string;
  onChange: (p: PhoneInputData) => unknown;
  fullWidth?: boolean;
  className?: string;
  countryCodeDisabled?: boolean;
  inputRef?: MutableRefObject<HTMLInputElement>;
} & Omit<TextFieldProps, 'onChange'>;

export type PhoneInputData = {
  raw: string;
  parsed?: PhoneNumber;
};

export const PhoneInput = (props: PhoneInputProps) => {
  const { value, error, helperText, onChange, fullWidth, className, inputRef, countryCodeDisabled, ...params } = props;

  const { country, language } = useLanguage();

  const countries = useMemo(() => getCountries(language), [language]);
  const defaultCountry = countries.find((c) => c.code === country);

  const [code, setCode] = useState(defaultCountry?.phoneCode);
  const [number, setNumber] = useState(null);
  const [countryIsoCode, setCountyIsoCode] = useState(country as string);

  const { t } = useTranslation();
  const phoneNumberLabel = t('common.phone-number') + ':';
  const countryCodeLabel = t('common.country-phone-code') + ':';

  useEffect(() => {
    let toParse = value;

    if (!toParse) {
      return;
    }

    if (toParse.startsWith('00')) {
      toParse = toParse.substring(2);
    }

    if (!toParse.startsWith('+')) {
      toParse = `+${toParse}`;
    }

    const parsed = parsePhoneNumber(toParse);

    if (parsed) {
      setCode(parsed.countryCallingCode);
      setNumber(parsed.nationalNumber);
      setCountyIsoCode(parsed.country);
      onChange({ raw: toParse, parsed });
    }
  }, [value]);

  const constructInputData = (code: string, number: string) => {
    const fullNumber = `+${code}${number}`;

    return {
      raw: number ? fullNumber : '',
      parsed: parsePhoneNumber(fullNumber),
    };
  };

  const onCodeChange = (_e: ChangeEvent<HTMLInputElement>, item: { code: string; name: string; phoneCode: string }) => {
    setCode(item.phoneCode);
    setCountyIsoCode(item.code);
    onChange(constructInputData(item.phoneCode, number));
  };

  const onNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    let newNumber = e.target.value;

    setNumber(newNumber);

    if (newNumber.startsWith('0')) {
      newNumber = newNumber.substring(1);
    }

    onChange(constructInputData(code, newNumber));
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        width: fullWidth ? '100%' : null,
      }}
      className={className}
    >
      <CountrySelect
        sx={{
          minWidth: '100px',
        }}
        mode="phoneCode"
        label={countryCodeLabel}
        countryCode={countryIsoCode}
        handleChange={onCodeChange}
        error={error}
        required={false}
        disabled={countryCodeDisabled}
      />
      <TextField
        inputRef={inputRef}
        sx={{ flex: 1 }}
        variant="standard"
        onChange={onNumberChange}
        {...params}
        label={phoneNumberLabel}
        value={number || ''}
        InputLabelProps={{
          shrink: true,
        }}
        error={error}
        helperText={helperText}
        required={false}
      />
    </Stack>
  );
};
